import React, { useState, useEffect } from "react";
import { Modal, Form, Button, Dropdown, TextArea, Checkbox } from "semantic-ui-react";
import PropTypes from "prop-types";

const COLOR_OPTIONS = [
  { key: "info", value: "info", text: "Info (Blue)" },
  { key: "warning", value: "warning", text: "Warning (Yellow)" },
  { key: "danger", value: "danger", text: "Danger (Red)" },
  { key: "success", value: "success", text: "Success (Green)" },
];

const DURATION_UNIT_OPTIONS = [
  { key: "hours", value: "hours", text: "Hours" },
  { key: "days", value: "days", text: "Days" },
];

const EXPIRATION_TYPE_DATETIME = "datetime";
const EXPIRATION_TYPE_DURATION = "duration";

const DEFAULT_COLOR = "info";
const EMPTY_DURATION_VALUE = "";
const DEFAULT_DURATION_UNIT = "hours";

function toLocalDatetimeInputValue(date) {
  const offsetMs = date.getTimezoneOffset() * 60 * 1000;
  return new Date(date.getTime() - offsetMs).toISOString().slice(0, 16);
}

function buildInitialState(announcement) {
  if (!announcement) {
    return {
      message: "",
      color: DEFAULT_COLOR,
      expirationType: EXPIRATION_TYPE_DATETIME,
      datetimeValue: "",
      durationValue: EMPTY_DURATION_VALUE,
      durationUnit: DEFAULT_DURATION_UNIT,
      slackEnabled: false,
    };
  }

  const hasDuration = announcement.durationValue != null && announcement.durationUnit != null;
  const expirationType = hasDuration ? EXPIRATION_TYPE_DURATION : EXPIRATION_TYPE_DATETIME;

  const datetimeValue =
    expirationType === EXPIRATION_TYPE_DATETIME && announcement.expiresAt
      ? toLocalDatetimeInputValue(new Date(announcement.expiresAt))
      : "";

  return {
    message: announcement.message || "",
    color: announcement.color || DEFAULT_COLOR,
    expirationType,
    datetimeValue,
    durationValue: announcement.durationValue != null ? String(announcement.durationValue) : EMPTY_DURATION_VALUE,
    durationUnit: announcement.durationUnit || DEFAULT_DURATION_UNIT,
    slackEnabled: announcement.slackEnabled || false,
  };
}

const SLACK_HELPER_STYLE = { fontSize: "0.85em", color: "#888", marginTop: 4, marginLeft: 28 };

function renderSlackHelperText(isEditing, slackEnabled) {
  let text = "Post this announcement to the #announcements Slack channel.";
  if (isEditing && slackEnabled) {
    text = "This announcement was posted to #announcements. Updates will sync automatically.";
  } else if (isEditing) {
    text = "This announcement was not posted to Slack.";
  }

  return <p style={SLACK_HELPER_STYLE}>{text}</p>;
}

function isFormValid(message, expirationType, datetimeValue, durationValue) {
  const hasMessage = message.trim().length > 0;
  const hasDatetime = expirationType === EXPIRATION_TYPE_DATETIME && datetimeValue.length > 0;
  const hasDuration = expirationType === EXPIRATION_TYPE_DURATION && Number(durationValue) > 0;
  return hasMessage && (hasDatetime || hasDuration);
}

const AnnouncementFormModal = ({ open, onClose, onSubmit, announcement, loading }) => {
  const [message, setMessage] = useState("");
  const [color, setColor] = useState(DEFAULT_COLOR);
  const [expirationType, setExpirationType] = useState(EXPIRATION_TYPE_DATETIME);
  const [datetimeValue, setDatetimeValue] = useState("");
  const [durationValue, setDurationValue] = useState(EMPTY_DURATION_VALUE);
  const [durationUnit, setDurationUnit] = useState(DEFAULT_DURATION_UNIT);
  const [slackEnabled, setSlackEnabled] = useState(false);

  useEffect(() => {
    const initial = buildInitialState(announcement);
    setMessage(initial.message);
    setColor(initial.color);
    setExpirationType(initial.expirationType);
    setDatetimeValue(initial.datetimeValue);
    setDurationValue(initial.durationValue);
    setDurationUnit(initial.durationUnit);
    setSlackEnabled(initial.slackEnabled);
  }, [announcement, open]);

  const isEditing = Boolean(announcement);

  const handleSubmit = () => {
    const data = { message: message.trim(), color, expirationType };

    if (expirationType === EXPIRATION_TYPE_DATETIME) {
      data.expiresAt = new Date(datetimeValue).toISOString();
    } else {
      data.durationValue = Number(durationValue);
      data.durationUnit = durationUnit;
    }

    if (!isEditing) {
      data.slackEnabled = slackEnabled;
    }

    onSubmit(data);
  };

  const isValid = isFormValid(message, expirationType, datetimeValue, durationValue);

  return (
    <Modal open={open} onClose={onClose} size="small">
      <Modal.Header>{isEditing ? "Edit Announcement" : "New Announcement"}</Modal.Header>
      <Modal.Content>
        <Form>
          <Form.Field required>
            <label>Message</label>
            <TextArea
              placeholder="Enter announcement message"
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              rows={3}
            />
          </Form.Field>

          <Form.Field required>
            <label>Color</label>
            <Dropdown
              selection
              options={COLOR_OPTIONS}
              value={color}
              onChange={(e, { value }) => setColor(value)}
            />
          </Form.Field>

          <Form.Field required>
            <label>Expiration Type</label>
            <Form.Group inline>
              <Form.Radio
                label="Specific date/time"
                value={EXPIRATION_TYPE_DATETIME}
                checked={expirationType === EXPIRATION_TYPE_DATETIME}
                onChange={() => setExpirationType(EXPIRATION_TYPE_DATETIME)}
              />
              <Form.Radio
                label="Duration from now"
                value={EXPIRATION_TYPE_DURATION}
                checked={expirationType === EXPIRATION_TYPE_DURATION}
                onChange={() => setExpirationType(EXPIRATION_TYPE_DURATION)}
              />
            </Form.Group>
          </Form.Field>

          {expirationType === EXPIRATION_TYPE_DATETIME && (
            <Form.Field required>
              <label>Expires At</label>
              <input
                type="datetime-local"
                value={datetimeValue}
                onChange={(e) => setDatetimeValue(e.target.value)}
              />
            </Form.Field>
          )}

          {expirationType === EXPIRATION_TYPE_DURATION && (
            <Form.Field required>
              <label>Duration</label>
              <Form.Group inline>
                <Form.Input
                  type="number"
                  min="1"
                  placeholder="e.g. 24"
                  value={durationValue}
                  onChange={(e) => setDurationValue(e.target.value)}
                  width={6}
                />
                <Form.Field width={6}>
                  <Dropdown
                    selection
                    options={DURATION_UNIT_OPTIONS}
                    value={durationUnit}
                    onChange={(e, { value }) => setDurationUnit(value)}
                  />
                </Form.Field>
              </Form.Group>
            </Form.Field>
          )}

          <Form.Field>
            <Checkbox
              label="Post to Slack"
              checked={slackEnabled}
              onChange={(e, { checked }) => setSlackEnabled(checked)}
              disabled={isEditing}
            />
            {renderSlackHelperText(isEditing, slackEnabled)}
          </Form.Field>
        </Form>
      </Modal.Content>
      <Modal.Actions>
        <Button onClick={onClose} disabled={loading} basic>
          Cancel
        </Button>
        <Button
          color="teal"
          onClick={handleSubmit}
          disabled={!isValid || loading}
          loading={loading}
        >
          Save
        </Button>
      </Modal.Actions>
    </Modal>
  );
};

AnnouncementFormModal.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onSubmit: PropTypes.func.isRequired,
  announcement: PropTypes.object,
  loading: PropTypes.bool,
};

AnnouncementFormModal.defaultProps = {
  announcement: null,
  loading: false,
};

export default AnnouncementFormModal;
